import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { AuthProvider } from '@/auth/authContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import AdminLayout from '@/components/layout/AdminLayout';
import NotFoundPage from '@/pages/NotFoundPage';
import LandingPage from '@/pages/LandingPage';
import EnhancedLoginPage from '@/pages/EnhancedLoginPage';
import RegisterDriverPage from '@/pages/RegisterDriverPage';
import RegisterPassengerPage from '@/pages/RegisterPassengerPage';
import RegisterAdminPage from '@/pages/RegisterAdminPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import Dashboard from '@/pages/Dashboard';
import DriversPage from '@/pages/DriversPage';
import AddDriverPage from '@/pages/AddDriverPage';
import EditDriverPage from '@/pages/EditDriverPage';
import CreateDriverWizardPage from '@/pages/CreateDriverWizardPage';
import DriverDetailsPage from '@/pages/DriverDetailsPage';
import RidersPage from '@/pages/RidersPage';
import AddRiderPage from '@/pages/AddRiderPage';
import EditRiderPage from '@/pages/EditRiderPage';
import BasePricingPage from '@/pages/BasePricingPage';
import AddEditPricingPage from '@/pages/AddEditPricingPage';
import ViewBasePricingPage from '@/pages/ViewBasePricingPage';
import BasePricingAnalyticsPage from '@/pages/BasePricingAnalyticsPage';
import SurgePricingPage from '@/pages/SurgePricingPage';
import AddEditSurgePricingPage from '@/pages/AddEditSurgePricingPage';
import ViewSurgePricingPage from '@/pages/ViewSurgePricingPage';
import EditSurgePricingPage from '@/pages/EditSurgePricingPage';
import PromotionsPage from '@/pages/PromotionsPage';
import ViewPromotionPage from '@/pages/ViewPromotionPage';
import AddEditPromotionPage from '@/pages/AddEditPromotionPage';
import ReferralCampaignsPage from '@/pages/ReferralCampaignsPage';
import ViewReferralCampaignPage from '@/pages/ViewReferralCampaignPage';
import AddEditReferralCampaignPage from '@/pages/AddEditReferralCampaignPage';
import VehicleCategoriesPage from '@/pages/VehicleCategoriesPage';
import AddEditVehicleCategoryPage from '@/pages/AddEditVehicleCategoryPage';
import ViewVehicleCategoryPage from '@/pages/ViewVehicleCategoryPage';
import DisputesPage from '@/pages/DisputesPage';
import AddDisputePage from '@/pages/AddDisputePage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import ReportsPage from '@/pages/ReportsPage';
import CountriesPage from '@/pages/geography/CountriesPage';
import StatesPage from '@/pages/geography/StatesPage';
import CitiesPage from '@/pages/geography/CitiesPage';
import UsersPage from '@/pages/users/UsersPage';
import AddUserPage from '@/pages/users/AddUserPage';
import EditUserPage from '@/pages/users/EditUserPage';
import RolesPage from '@/pages/users/RolesPage';
import AddEditRolePage from '@/pages/users/AddEditRolePage';
import PermissionsPage from '@/pages/users/PermissionsPage';
import PricingPage from '@/pages/PricingPage';
import RequestDemo from '@/pages/RequestDemo';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="aboosto-theme">
        <AuthProvider>
          <ErrorBoundary>
            <Router>
              <div className="min-h-screen bg-background text-foreground transition-colors">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<EnhancedLoginPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/requestdemo" element={<RequestDemo />} />
                  <Route path="/register/driver" element={<RegisterDriverPage />} />
                  <Route path="/register/passenger" element={<RegisterPassengerPage />} />
                  <Route path="/register/admin" element={<RegisterAdminPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />

                  {/* Protected Config Management Routes - Authentication Required (with AdminLayout) */}
                  <Route path="/admin/config" element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/admin/config/countries" replace />} />
                    <Route path="countries" element={<CountriesPage />} />
                    <Route path="states" element={<StatesPage />} />
                    <Route path="cities" element={<CitiesPage />} />
                  </Route>

                  {/* Protected Admin Routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute requiredRole={'ROLE_SUPER_ADMIN'}>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="drivers">
                      <Route index element={<DriversPage />} />
                      <Route path="add" element={<AddDriverPage />} />
                      <Route path="create" element={<CreateDriverWizardPage />} />
                      <Route path="edit/:driverId" element={<EditDriverPage />} />
                      <Route path=":id" element={<DriverDetailsPage />} />
                    </Route>
                    <Route path="riders">
                      <Route index element={<RidersPage />} />
                      <Route path="add" element={<AddRiderPage />} />
                      <Route path="edit/:riderId" element={<EditRiderPage />} />
                    </Route>
                    <Route path="base-pricing">
                      <Route index element={<BasePricingPage />} />
                      <Route path="add" element={<AddEditPricingPage />} />
                      <Route path="edit/:pricingId" element={<AddEditPricingPage />} />
                      <Route path="view/:pricingId" element={<ViewBasePricingPage />} />
                      <Route path="analytics" element={<BasePricingAnalyticsPage />} />
                    </Route>
                    <Route path="vehicle-categories">
                      <Route index element={<VehicleCategoriesPage />} />
                      <Route path="add" element={<AddEditVehicleCategoryPage />} />
                      <Route path="edit/:categoryId" element={<AddEditVehicleCategoryPage />} />
                      <Route path="view/:categoryId" element={<ViewVehicleCategoryPage />} />
                    </Route>
                    <Route path="surge-pricing">
                      <Route index element={<SurgePricingPage />} />
                      <Route path="add" element={<AddEditSurgePricingPage />} />
                      <Route path="view/:id" element={<ViewSurgePricingPage />} />
                      <Route path="edit/:id" element={<EditSurgePricingPage />} />
                    </Route>
                    <Route path="promotions">
                      <Route index element={<PromotionsPage />} />
                      <Route path="add" element={<AddEditPromotionPage />} />
                      <Route path="view/:id" element={<ViewPromotionPage />} />
                      <Route path="edit/:id" element={<AddEditPromotionPage />} />
                      {/* TODO: Add PromotionsAnalyticsPage */}
                    </Route>
                    <Route path="referral-campaigns">
                      <Route index element={<ReferralCampaignsPage />} />
                      <Route path="add" element={<AddEditReferralCampaignPage />} />
                      <Route path="view/:id" element={<ViewReferralCampaignPage />} />
                      <Route path="edit/:id" element={<AddEditReferralCampaignPage />} />
                      {/* TODO: Add ReferralCampaignsAnalyticsPage */}
                    </Route>
                    <Route path="disputes">
                      <Route index element={<DisputesPage />} />
                      <Route path="add" element={<AddDisputePage />} />
                    </Route>
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="reports" element={<ReportsPage />} />

                    {/* User Management - Admin only */}
                    <Route path="users">
                      <Route index element={<Navigate to="/admin/users/list" replace />} />
                      <Route
                        path="list"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'user', action: 'read' }}>
                            <UsersPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="add"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'user', action: 'create' }}>
                            <AddUserPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="edit/:userId"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'user', action: 'update' }}>
                            <EditUserPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="roles"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'system', action: 'manage_permissions' }}>
                            <RolesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="roles/add"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'system', action: 'manage_permissions' }}>
                            <AddEditRolePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="roles/edit/:id"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'system', action: 'manage_permissions' }}>
                            <AddEditRolePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="permissions"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'system', action: 'manage_permissions' }}>
                            <PermissionsPage />
                          </ProtectedRoute>
                        }
                      />
                    </Route>

                    {/* Security */}
                    <Route path="security">
                      <Route index element={<Navigate to="/admin/security/change-password" replace />} />
                      <Route path="change-password" element={<ChangePasswordPage />} />
                    </Route>
                  </Route>

                  {/* Driver Routes (to be implemented) */}
                  <Route path="/driver" element={
                    <ProtectedRoute requiredRole="driver" fallbackPath="/login">
                      <div>Driver Dashboard - Coming Soon</div>
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/driver/dashboard" replace />} />
                    <Route path="dashboard" element={<div>Driver Dashboard - Coming Soon</div>} />
                  </Route>

                  {/* Passenger Routes (to be implemented) */}
                  <Route path="/passenger" element={
                    <ProtectedRoute requiredRole="passenger" fallbackPath="/login">
                      <div>Passenger Dashboard - Coming Soon</div>
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/passenger/dashboard" replace />} />
                    <Route path="dashboard" element={<div>Passenger Dashboard - Coming Soon</div>} />
                  </Route>

                  {/* Legacy Routes (redirect to new paths) */}
                  <Route path="/register/rider" element={<Navigate to="/register/passenger" replace />} />
                  <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/drivers" element={<Navigate to="/admin/drivers" replace />} />
                  <Route path="/passengers" element={<Navigate to="/admin/riders" replace />} />
                  <Route path="/riders" element={<Navigate to="/admin/riders" replace />} />
                  <Route path="/fares" element={<Navigate to="/admin/base-pricing" replace />} />
                  <Route path="/pricing" element={<Navigate to="/admin/base-pricing" replace />} />
                  <Route path="/disputes" element={<Navigate to="/admin/disputes" replace />} />
                  <Route path="/analytics" element={<Navigate to="/admin/analytics" replace />} />
                  <Route path="/reports" element={<Navigate to="/admin/reports" replace />} />

                  {/* 404 Not Found Route */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <Toaster />
              </div>
            </Router>
          </ErrorBoundary>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;